import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";

import { SUPPORTED_LOCALES } from "@/i18n/config";
import toast from "react-hot-toast";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import { Label } from "@/components/ui/label";
import { LoadingLogo } from "@/components/shared/LoadingLogo";
import { PasswordStrengthIndicator } from "@/components/shared/PasswordStrengthIndicator";
import { GoogleSsoButton } from "@/components/auth/GoogleSsoButton";
import { apiBaseUrl } from "@/services/apiClient";
import {
  getTenantInvitationByToken,
  acceptTenantInvitation,
  acceptTenantInvitationExistingUser,
  applyVerifyResponse,
} from "@/services/authService";
import type { TenantInvitationInfo } from "@/services/authService";
import { useAuth } from "@/contexts/AuthContext";

function sameEmail(a: string, b: string): boolean {
  return a.trim().toLowerCase() === b.trim().toLowerCase();
}

export function AcceptInvitationPage(): JSX.Element {
  const { t, i18n } = useTranslation();
  const { token: tokenFromPath } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();
  const tokenFromQuery = searchParams.get("token");
  const token = tokenFromPath ?? tokenFromQuery ?? null;
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();

  const [invitation, setInvitation] = useState<TenantInvitationInfo | null>(null);
  const [loadingInvitation, setLoadingInvitation] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!token) {
      setLoadingInvitation(false);
      setErrorMessage(t("auth.acceptInvitation.tokenMissing"));
      return;
    }
    setLoadingInvitation(true);
    setErrorMessage(null);
    getTenantInvitationByToken(token)
      .then((inv) => {
        setInvitation(inv);
        if (
          inv.language &&
          (SUPPORTED_LOCALES as readonly string[]).includes(inv.language) &&
          i18n.language !== inv.language
        ) {
          void i18n.changeLanguage(inv.language);
        }
      })
      .catch(() => {
        setErrorMessage(t("auth.acceptInvitation.invalidToken"));
      })
      .finally(() => {
        setLoadingInvitation(false);
      });
  }, [token, t]);

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!token) {
      setErrorMessage(t("auth.acceptInvitation.tokenMissing"));
      return;
    }
    if (password !== confirmPassword) {
      toast.error(t("auth.acceptInvitation.passwordMismatch"));
      return;
    }
    if (password.length < 6) {
      toast.error(t("auth.acceptInvitation.passwordMin"));
      return;
    }
    setErrorMessage(null);
    setIsSubmitting(true);
    try {
      const response = await acceptTenantInvitation(token, {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        password,
      });
      applyVerifyResponse(response);
      await refreshUser();
      toast.success(
        t("auth.acceptInvitation.successAccepted", {
          tenantName:
            response.user.tenantName ??
            invitation?.tenantName ??
            invitation?.tenantLegalName ??
            "",
        })
      );
      navigate("/", { replace: true });
    } catch (err: unknown) {
      const status = err && typeof err === "object" && "response" in err
        ? (err as { response?: { status?: number } }).response?.status
        : undefined;
      if (status === 409) {
        toast.error(t("auth.acceptInvitation.errorUserExists"));
      }
      setErrorMessage(t("auth.acceptInvitation.error"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAcceptExisting = async (): Promise<void> => {
    if (!token) return;
    setErrorMessage(null);
    setIsSubmitting(true);
    try {
      const response = await acceptTenantInvitationExistingUser(token);
      applyVerifyResponse(response);
      await refreshUser();
      toast.success(
        t("auth.acceptInvitation.successAccepted", {
          tenantName:
            response.user.tenantName ??
            invitation?.tenantName ??
            invitation?.tenantLegalName ??
            "",
        })
      );
      navigate("/", { replace: true });
    } catch {
      setErrorMessage(t("auth.acceptInvitation.error"));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loadingInvitation) {
    return (
      <div className="h-svh overflow-y-auto bg-muted/40">
        <div className="flex min-h-full flex-col items-center justify-center p-6">
        <Card className="w-full max-w-md">
          <CardContent className="flex flex-col items-center gap-3 p-8">
            <LoadingLogo size="sm" />
            <p className="text-sm text-muted-foreground">
              {t("auth.acceptInvitation.loading")}
            </p>
          </CardContent>
        </Card>
        </div>
      </div>
    );
  }

  if (!token || errorMessage || !invitation) {
    return (
      <div className="h-svh overflow-y-auto bg-muted/40">
        <div className="flex min-h-full flex-col items-center justify-center p-6">
        <Card className="w-full max-w-md">
          <CardContent className="p-6">
            <h1 className="text-2xl font-bold text-foreground">
              {t("auth.acceptInvitation.title")}
            </h1>
            <p className="mt-2 text-muted-foreground">
              {errorMessage ?? t("auth.acceptInvitation.tokenMissing")}
            </p>
            <Button asChild variant="outline" className="mt-4 w-full">
              <Link to="/login">{t("auth.acceptInvitation.backToLogin")}</Link>
            </Button>
          </CardContent>
        </Card>
        </div>
      </div>
    );
  }

  const tenantName =
    invitation.tenantLegalName ?? invitation.tenantName ?? "";
  const isExistingUser = Boolean(invitation.userAlreadyExists);
  const isLoggedInAsInvited =
    isExistingUser &&
    user != null &&
    sameEmail(user.email, invitation.email);
  // User came back from an SSO bounce signed in as someone else (or just
  // happens to be logged into another account in this browser). Surface
  // it directly instead of dropping them into the "create password" form
  // — that form would create yet another mismatched user.
  const isAuthedAsDifferentEmail =
    user != null && !sameEmail(user.email, invitation.email);

  const acceptReturnPath = token
    ? `/accept-invitation?token=${token}`
    : "/accept-invitation";
  const loginReturnUrl = token
    ? `/login?returnTo=${encodeURIComponent(acceptReturnPath)}`
    : "/login";

  return (
    <div className="h-svh overflow-y-auto bg-muted/40">
      <div className="flex min-h-full flex-col items-center justify-center p-6">
      <Card className="w-full max-w-md">
        <CardContent className="p-6">
          <h1 className="text-2xl font-bold text-foreground">
            {t("auth.acceptInvitation.title")}
          </h1>
          <p className="mt-2 text-muted-foreground">
            {isExistingUser && isLoggedInAsInvited
              ? t("auth.acceptInvitation.descriptionExistingUser", {
                  tenantName,
                })
              : isExistingUser
                ? t("auth.acceptInvitation.existingUserSignInPrompt")
                : t("auth.acceptInvitation.description", { tenantName })}
          </p>
          {invitation && (
            <div className="mt-4 flex items-center gap-3 rounded-lg border bg-muted/50 p-4">
              {invitation.tenantProfileImageUrl != null &&
              invitation.tenantProfileImageUrl !== "" ? (
                <Avatar className="h-12 w-12 shrink-0 rounded-lg">
                  <AvatarImage
                    src={
                      invitation.tenantProfileImageUrl.startsWith("http")
                        ? invitation.tenantProfileImageUrl
                        : `${apiBaseUrl}${invitation.tenantProfileImageUrl}`
                    }
                    alt=""
                  />
                  <AvatarFallback className="rounded-lg text-sm font-medium">
                    {(invitation.tenantLegalName ?? invitation.tenantName).slice(
                      0,
                      2
                    )}
                  </AvatarFallback>
                </Avatar>
              ) : null}
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-foreground">
                  {invitation.tenantLegalName ?? invitation.tenantName}
                </p>
                <p className="text-xs text-muted-foreground">
                  {t("auth.acceptInvitation.invitedAs")} {invitation.email}
                </p>
              </div>
            </div>
          )}

          {isAuthedAsDifferentEmail ? (
            <div className="mt-6 flex flex-col gap-4">
              <p className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                {t(
                  "auth.acceptInvitation.wrongAccountSignedIn",
                  "Esta invitación es para {{invitedEmail}}, pero iniciaste sesión como {{currentEmail}}. Cerrá sesión y volvé a abrir el enlace con la cuenta correcta.",
                  {
                    invitedEmail: invitation.email,
                    currentEmail: user?.email ?? "",
                  },
                )}
              </p>
              <Button asChild variant="outline" className="w-full">
                <Link to="/login">
                  {t("auth.acceptInvitation.backToLogin")}
                </Link>
              </Button>
            </div>
          ) : isExistingUser && isLoggedInAsInvited ? (
            <div className="mt-6 flex flex-col gap-4">
              {errorMessage && (
                <p className="text-sm text-destructive">{errorMessage}</p>
              )}
              <Button
                type="button"
                onClick={() => void handleAcceptExisting()}
                disabled={isSubmitting}
                className="h-auto min-h-10 w-full whitespace-normal py-2.5 text-center leading-snug"
              >
                {isSubmitting
                  ? t("auth.acceptInvitation.submittingSwitch")
                  : t("auth.acceptInvitation.acceptAndSwitch", {
                      tenantName,
                    })}
              </Button>
              <Button asChild variant="ghost" className="w-full">
                <Link to={loginReturnUrl}>
                  {t("auth.acceptInvitation.backToLogin")}
                </Link>
              </Button>
            </div>
          ) : isExistingUser ? (
            <div className="mt-6 flex flex-col gap-4">
              <Button asChild className="w-full">
                <Link to={loginReturnUrl}>
                  {t("auth.acceptInvitation.signIn")}
                </Link>
              </Button>
              <div className="flex items-center gap-3 text-xs uppercase text-muted-foreground">
                <span className="h-px flex-1 bg-border" aria-hidden />
                {t("auth.login.divider", "o bien")}
                <span className="h-px flex-1 bg-border" aria-hidden />
              </div>
              <GoogleSsoButton intent="business" returnTo={acceptReturnPath} />
              <Button asChild variant="ghost" className="w-full">
                <Link to="/login">
                  {t("auth.acceptInvitation.backToLogin")}
                </Link>
              </Button>
            </div>
          ) : (
            <>
            <div className="mt-6 flex flex-col gap-3">
              <GoogleSsoButton intent="business" returnTo={acceptReturnPath} />
              <div className="flex items-center gap-3 text-xs uppercase text-muted-foreground">
                <span className="h-px flex-1 bg-border" aria-hidden />
                {t("auth.login.divider", "o bien")}
                <span className="h-px flex-1 bg-border" aria-hidden />
              </div>
            </div>
            <form onSubmit={handleSubmit} className="mt-4 flex flex-col gap-4">
              <div className="grid gap-2">
                <Label htmlFor="firstName">
                  {t("auth.acceptInvitation.firstName")}
                </Label>
                <Input
                  id="firstName"
                  type="text"
                  autoComplete="given-name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  required
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="lastName">
                  {t("auth.acceptInvitation.lastName")}
                </Label>
                <Input
                  id="lastName"
                  type="text"
                  autoComplete="family-name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  required
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="password">
                  {t("auth.acceptInvitation.password")}
                </Label>
                <PasswordInput
                  id="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  minLength={6}
                  required
                />
                <PasswordStrengthIndicator
                  password={password}
                  showRules
                  rulesLabels={{
                    minLength: t("auth.register.passwordStrength.minLength"),
                    uppercase: t("auth.register.passwordStrength.uppercase"),
                    number: t("auth.register.passwordStrength.number"),
                    special: t("auth.register.passwordStrength.special"),
                  }}
                  strengthLabels={{
                    weak: t("auth.register.passwordStrength.weak"),
                    fair: t("auth.register.passwordStrength.fair"),
                    good: t("auth.register.passwordStrength.good"),
                    strong: t("auth.register.passwordStrength.strong"),
                  }}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="confirmPassword">
                  {t("auth.acceptInvitation.confirmPassword")}
                </Label>
                <PasswordInput
                  id="confirmPassword"
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  minLength={6}
                  required
                />
              </div>
              {errorMessage && (
                <p className="text-sm text-destructive">{errorMessage}</p>
              )}
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting
                  ? t("auth.acceptInvitation.submitting")
                  : t("auth.acceptInvitation.submit")}
              </Button>
              <Button asChild variant="ghost" className="w-full">
                <Link to="/login">
                  {t("auth.acceptInvitation.backToLogin")}
                </Link>
              </Button>
            </form>
            </>
          )}
        </CardContent>
      </Card>
      </div>
    </div>
  );
}
